import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  ChevronRight,
  Cpu,
  Loader2,
  Plus,
  Radio,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import {
  type Device,
  type DeviceWithSensors,
  type Sensor,
  deleteDevice,
  getDevice,
  updateDevice,
} from "../../lib/instrumentationApi";
import { formatDate } from "../../lib/utils";
import { SearchableAssetPicker, type AssetOption } from "./SearchableAssetPicker";
import { SOURCE_LOGOS } from "./sourceLogos";
import { DMSDocumentsPanel } from "../dms/DMSDocumentsPanel";

interface DeviceDetailDrawerProps {
  device: Device;
  assets: AssetOption[];
  onClose: () => void;
  /** Bubble up so the page's device list refreshes after edits/deletes. */
  onChanged: () => void;
  onDeleted: () => void;
  /** Open the Add Sensor modal for this device. */
  onAddSensor: () => void;
  /** Open the sensor in the right sidebar (replaces this drawer's content). */
  onSelectSensor: (sensor: Sensor, device: Device) => void;
  /** Layout mode mirroring SensorDetailDrawer — "drawer" pins to the right
   *  edge as an overlay, "pane" fills the parent flex slot. The
   *  Instrumentation page uses "pane". */
  variant?: "drawer" | "pane";
}

/** Right-sidebar editor for a device. Replaces the previous "Edit device"
 *  modal — the user explicitly wanted device edits to happen inline.
 *  Surfaces metadata, source connection settings (read-only summary), the
 *  linked asset (searchable picker), and the device's child sensors with
 *  click-through. Delete sits at the bottom behind a confirm dialog. */
export function DeviceDetailDrawer({
  device: initialDevice,
  assets,
  onClose,
  onChanged,
  onDeleted,
  onAddSensor,
  onSelectSensor,
  variant = "pane",
}: DeviceDetailDrawerProps) {
  // Local copy so on-screen state stays consistent if the parent device
  // prop re-renders mid-edit.
  const [device, setDevice] = useState<Device>(initialDevice);
  const [sensors, setSensors] = useState<Sensor[] | null>(null);

  // Editable fields — mirror Device's metadata. Source type + config are
  // edited via the create wizard for now; this form covers metadata + asset
  // linkage which is the common path.
  const [name, setName] = useState(initialDevice.name);
  const [description, setDescription] = useState(initialDevice.description ?? "");
  const [deviceType, setDeviceType] = useState(initialDevice.device_type ?? "");
  const [manufacturer, setManufacturer] = useState(initialDevice.manufacturer ?? "");
  const [model, setModel] = useState(initialDevice.model ?? "");
  const [serial, setSerial] = useState(initialDevice.serial_number ?? "");
  const [status, setStatus] = useState(initialDevice.status);
  const [assetId, setAssetId] = useState<string>(initialDevice.asset_id ?? "");

  const [isSaving, setIsSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    // Re-seed when the parent switches to a different device. Keyed on id
    // so live source/connection updates from the list refresh don't
    // clobber the user's mid-edit changes.
    setDevice(initialDevice);
    setName(initialDevice.name);
    setDescription(initialDevice.description ?? "");
    setDeviceType(initialDevice.device_type ?? "");
    setManufacturer(initialDevice.manufacturer ?? "");
    setModel(initialDevice.model ?? "");
    setSerial(initialDevice.serial_number ?? "");
    setStatus(initialDevice.status);
    setAssetId(initialDevice.asset_id ?? "");
    setError(null);
    setSavedAt(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDevice.id]);

  // Fetch sensors when the drawer opens or device changes. Keeps the
  // "Sensors on this device" list fresh.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const detail: DeviceWithSensors = await getDevice(initialDevice.id);
        if (!cancelled) setSensors(detail.sensors);
      } catch {
        if (!cancelled) setSensors([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialDevice.id]);

  // True when any editable field diverges from the saved device.
  const isDirty = useMemo(() => {
    return (
      name !== device.name ||
      (description ?? "") !== (device.description ?? "") ||
      (deviceType ?? "") !== (device.device_type ?? "") ||
      (manufacturer ?? "") !== (device.manufacturer ?? "") ||
      (model ?? "") !== (device.model ?? "") ||
      (serial ?? "") !== (device.serial_number ?? "") ||
      status !== device.status ||
      (assetId ?? "") !== (device.asset_id ?? "")
    );
  }, [
    name,
    description,
    deviceType,
    manufacturer,
    model,
    serial,
    status,
    assetId,
    device,
  ]);

  async function handleSave() {
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        name: name.trim(),
        description: description.trim() || null,
        device_type: deviceType.trim() || null,
        manufacturer: manufacturer.trim() || null,
        model: model.trim() || null,
        serial_number: serial.trim() || null,
        status,
      };
      if ((assetId || "") !== (device.asset_id ?? "")) {
        // Only forward when the user changed it. The Pydantic update
        // schema treats absent fields as "leave unchanged".
        payload.asset_id = assetId || null;
      }
      const fresh = await updateDevice(device.id, payload);
      setDevice(fresh);
      setSavedAt(Date.now());
      onChanged();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e.response?.data?.detail || "Failed to save device.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete() {
    setIsDeleting(true);
    try {
      await deleteDevice(device.id);
      onDeleted();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e.response?.data?.detail || "Failed to delete device.");
    } finally {
      setIsDeleting(false);
    }
  }

  const outerClass =
    variant === "drawer"
      ? "absolute inset-y-0 right-0 z-30 flex w-[480px] max-w-[90vw] flex-col border-l border-surface-700/60 bg-surface-950 shadow-2xl"
      : "flex h-full w-full flex-col bg-surface-950";

  const Logo = SOURCE_LOGOS[device.source_type];

  return (
    <div className={outerClass}>
      <div className="flex items-center gap-3 border-b border-surface-700/60 px-4 py-3">
        {Logo ? (
          <Logo size={36} className="shrink-0 rounded-lg ring-1 ring-white/10" />
        ) : (
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500/30 to-purple-500/30 ring-1 ring-white/10">
            <Cpu className="h-4 w-4 text-brand-200" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-sm font-semibold text-surface-50">
            {device.name}
          </h2>
          <p className="truncate text-[11px] text-surface-400">
            {device.source_type.replace(/_/g, " ")}
            {device.device_type ? ` · ${device.device_type}` : ""}
            {device.sensor_count ? ` · ${device.sensor_count} sensor${device.sensor_count === 1 ? "" : "s"}` : ""}
          </p>
        </div>
        <button
          onClick={onClose}
          className="rounded-md p-1.5 text-surface-400 hover:bg-white/5 hover:text-surface-100"
          title="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="space-y-4 p-4">
          <div className="space-y-3 rounded-lg border border-surface-700/50 bg-surface-900/40 p-3">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-surface-500">
                Device metadata
              </span>
              {device.connection_status && (
                <ConnectionPill
                  status={device.connection_status}
                  lastSeen={device.last_seen_at}
                />
              )}
            </div>
            <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} disabled={isSaving} />
            <Input
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isSaving}
            />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Type" value={deviceType} onChange={(e) => setDeviceType(e.target.value)} disabled={isSaving} />
              <Input label="Status" value={status} onChange={(e) => setStatus(e.target.value)} disabled={isSaving} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Manufacturer" value={manufacturer} onChange={(e) => setManufacturer(e.target.value)} disabled={isSaving} />
              <Input label="Model" value={model} onChange={(e) => setModel(e.target.value)} disabled={isSaving} />
            </div>
            <Input label="Serial number" value={serial} onChange={(e) => setSerial(e.target.value)} disabled={isSaving} />
            <SearchableAssetPicker
              label="Linked asset"
              assets={assets}
              value={assetId}
              onChange={(id) => setAssetId(id ?? "")}
              disabled={isSaving}
              help="Same-site assets only. New child sensors inherit this asset by default."
            />
            {error && <div className="text-xs text-red-400">{error}</div>}
            <div className="flex items-center justify-between pt-1">
              {savedAt && Date.now() - savedAt < 5000 ? (
                <span className="text-[11px] text-emerald-300">Saved.</span>
              ) : (
                <span />
              )}
              <Button onClick={handleSave} isLoading={isSaving} disabled={!isDirty}>
                Save changes
              </Button>
            </div>
          </div>

          {/* Source connection summary — read-only for now. Editing source
              config inline would duplicate the wizard's per-source forms;
              users who need to reconfigure can recreate the device. */}
          <div className="rounded-lg border border-surface-700/50 bg-surface-900/40 p-3">
            <div className="mb-1.5 flex items-center gap-2">
              {Logo && <Logo size={20} className="rounded ring-1 ring-white/10" />}
              <span className="text-[10px] font-semibold uppercase tracking-wider text-surface-500">
                Source · {device.source_type.replace(/_/g, " ")}
              </span>
            </div>
            {device.source_config && Object.keys(device.source_config).length > 0 ? (
              <dl className="space-y-1 text-[11px]">
                {Object.entries(device.source_config).map(([k, v]) => (
                  <div key={k} className="flex items-baseline justify-between gap-2">
                    <dt className="text-surface-500">{k.replace(/_/g, " ")}</dt>
                    <dd className="truncate font-mono text-surface-200">
                      {redactIfSecret(k, v)}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="text-[11px] text-surface-500">
                No connection settings configured.
              </p>
            )}
          </div>

          {/* Sensors on this device */}
          <div className="rounded-lg border border-surface-700/50 bg-surface-900/40 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-surface-500">
                Sensors ({sensors?.length ?? "…"})
              </span>
              <Button size="sm" variant="secondary" onClick={onAddSensor}>
                <Plus className="mr-1 h-3.5 w-3.5" />
                Add sensor
              </Button>
            </div>
            {sensors === null ? (
              <div className="flex items-center gap-2 py-2 text-[11px] text-surface-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading sensors…
              </div>
            ) : sensors.length === 0 ? (
              <p className="py-2 text-center text-[11px] text-surface-500">
                No sensors yet. Add the first one.
              </p>
            ) : (
              <div className="space-y-1.5">
                {sensors.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => onSelectSensor(s, device)}
                    className="flex w-full items-center gap-2 rounded-md border border-surface-700/40 bg-surface-900/60 px-2 py-1.5 text-left transition-colors hover:border-brand-500/40 hover:bg-brand-500/5"
                  >
                    <Radio className="h-3.5 w-3.5 shrink-0 text-cyan-300" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="truncate text-xs font-medium text-surface-100">
                          {s.name}
                        </span>
                        {s.sensor_type && (
                          <span className="rounded bg-surface-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-surface-300">
                            {s.sensor_type}
                          </span>
                        )}
                      </div>
                      {s.unit && (
                        <span className="text-[10px] text-surface-500">{s.unit}</span>
                      )}
                    </div>
                    <ChevronRight className="h-3.5 w-3.5 shrink-0 text-surface-500" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* DMS Documents (Phase 22) */}
          <DMSDocumentsPanel
            virtualSiteId={device.virtual_site_id}
            entityType="device"
            entityId={device.id}
            defaultFolderPath={`Instrumentation/${(device.serial_number ?? device.name).trim()}`}
            title="DMS Documents"
          />

          {/* Danger zone */}
          <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3">
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-red-300">
              Danger zone
            </div>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-200 hover:bg-red-500/20"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete device
            </button>
            <p className="mt-1 text-[10px] text-red-200/70">
              Removes the device and every sensor on it. JSONB readings are also discarded.
            </p>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete device?"
        message={`Delete ${device.name}?`}
        detail="This removes the device and all of its sensors. Sensor readings stored in JSONB are also discarded."
        confirmLabel="Delete device"
        cancelLabel="Cancel"
        tone="destructive"
        isLoading={isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
}

/** Redact secret-shaped values in the source-config readout. Anything keyed
 *  with `password`, `secret`, `token`, `key`, or `connection_string` shows
 *  as dots so a screen-share doesn't leak credentials. */
function redactIfSecret(key: string, value: unknown): string {
  const sensitive = /(password|secret|token|key|connection_string)/i.test(key);
  if (value === null || value === undefined) return "—";
  const str = typeof value === "string" ? value : JSON.stringify(value);
  if (sensitive && str) return "•".repeat(Math.min(str.length, 16));
  return str;
}

function ConnectionPill({
  status,
  lastSeen,
}: {
  status: string;
  lastSeen: string | null;
}) {
  const tone =
    status === "connected"
      ? "bg-emerald-500/15 text-emerald-300"
      : status === "error"
        ? "bg-red-500/15 text-red-300"
        : "bg-surface-800 text-surface-400";
  return (
    <span
      className={
        "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider " +
        tone
      }
      title={lastSeen ? `Last seen ${lastSeen}` : "No connection telemetry"}
    >
      <Activity className="h-2.5 w-2.5" />
      {status}
      {lastSeen && (
        <span className="font-mono text-[9px] text-surface-500">
          {formatDate(lastSeen)}
        </span>
      )}
    </span>
  );
}
